import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Title } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Chip,
  TextField,
  Alert,
} from "@mui/material";
import { Search, CheckCircle, Cancel } from "@mui/icons-material";
import { StatusChip } from "../../../../components/StatusChip";
import { API, fetchJson } from "../../api";

interface Stats {
  roundId: string | null;
  totalBets: number;
  activeBets: number;
  autocashouts: number;
}

interface RoundData {
  roundId: string;
  state: string;
  crashPoint: number;
  currentMultiplier: number;
  initializedAt: string;
  startedAt: string | null;
  crashedAt: string | null;
}

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color?: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      gap: 1.5,
      px: 2,
      py: 1.5,
      bgcolor: "#1e293b",
      borderLeft: "3px solid",
      borderColor: color ?? "#64748b",
    }}
  >
    <Typography
      sx={{
        fontSize: "1.5rem",
        fontWeight: 700,
        color: color ?? "#f1f5f9",
        fontFamily: "'JetBrains Mono', monospace",
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.7rem",
        color: "#64748b",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 1,
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const FdbDashboard = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [round, setRound] = useState<RoundData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [requestChecking, setRequestChecking] = useState(false);
  const [requestResult, setRequestResult] = useState<boolean | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setLoading(true);
    setStats(null);
    setRound(null);
    const load = async () => {
      try {
        const [s, r] = await Promise.all([
          fetchJson<Stats>(`${API}/fdb/stats`),
          fetchJson<RoundData>(`${API}/fdb/rounds/current`),
        ]);
        if (s) setStats(s);
        if (r) setRound(r);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [location.key]);

  const handleSearch = async () => {
    const id = searchId.trim();
    if (!id) return;
    setSearchError(null);
    setSearching(true);
    try {
      const data = await fetchJson<RoundData>(`${API}/fdb/rounds/${id}`);
      if (data) {
        navigate(`/crash/fdb/rounds/${id}`);
        return;
      }
      setSearchError(`Round "${id}" not found in FDB cache`);
    } finally {
      setSearching(false);
    }
  };

  const handleRequestIdCheck = async () => {
    const rid = requestId.trim();
    if (!rid) return;
    setRequestResult(null);
    setRequestError(null);
    setRequestChecking(true);
    try {
      const data = await fetchJson<boolean>(`${API}/fdb/requests/${rid}`);
      if (data !== null) {
        setRequestResult(data);
      } else {
        setRequestError("Failed to check request ID");
      }
    } finally {
      setRequestChecking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title="FDB Dashboard" />
      <Typography
        sx={{
          fontSize: "1.25rem",
          fontWeight: 700,
          color: "#e2e8f0",
          mb: 3,
          letterSpacing: -0.3,
        }}
      >
        FDB Cache Browser
      </Typography>

      {/* Round search */}
      <Card sx={{ mb: 3, bgcolor: "#1e293b", borderLeft: "3px solid #7c3aed" }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.65rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 1,
              mb: 1,
              display: "block",
            }}
          >
            Look up any round by ID
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              placeholder="Enter Round ID..."
              value={searchId}
              onChange={(e) => {
                setSearchId(e.target.value);
                setSearchError(null);
              }}
              size="small"
              sx={{ flex: 1, maxWidth: 480 }}
              InputProps={{
                sx: {
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.85rem",
                },
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={searching || !searchId.trim()}
              startIcon={
                searching ? <CircularProgress size={16} /> : <Search />
              }
            >
              Browse
            </Button>
          </Box>
          {searchError && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
              {searchError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Current round */}
      <Typography
        sx={{
          color: "#64748b",
          fontSize: "0.65rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 1,
          mb: 1,
          display: "block",
        }}
      >
        Current Round
      </Typography>

      {round ? (
        <Card
          sx={{
            mb: 3,
            bgcolor: "#1e293b",
            borderLeft: "3px solid #7c3aed",
            cursor: "pointer",
            "&:hover": { borderColor: "#7c3aed" },
          }}
          onClick={() => navigate(`/crash/fdb/rounds/${round.roundId}`)}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                Round:
              </Typography>
              <Chip
                label={round.roundId}
                size="small"
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.8rem",
                }}
              />
              <StatusChip value={round.state} />
            </Box>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                Crash Point:{" "}
                <b style={{ color: "#f87171" }}>{round.crashPoint}x</b>
              </Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                Current:{" "}
                <b style={{ color: "#34d399" }}>{round.currentMultiplier}x</b>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No current round in FDB
        </Alert>
      )}

      {/* Stats for current round */}
      {stats && stats.roundId && (
        <>
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.65rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 1,
              mb: 1,
              display: "block",
            }}
          >
            Current Round Stats
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, md: 4 }}>
              <StatCard
                label="Total Bets"
                value={stats.totalBets}
                color="#60a5fa"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <StatCard
                label="Active Bets"
                value={stats.activeBets}
                color="#34d399"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <StatCard
                label="Autocashouts"
                value={stats.autocashouts}
                color="#fbbf24"
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Request ID check */}
      <Card sx={{ mb: 3, bgcolor: "#1e293b", borderLeft: "3px solid #7c3aed" }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.65rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 1,
              mb: 1,
              display: "block",
            }}
          >
            Check idempotency request ID
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              placeholder="Enter Request ID..."
              value={requestId}
              onChange={(e) => {
                setRequestId(e.target.value);
                setRequestResult(null);
                setRequestError(null);
              }}
              size="small"
              sx={{ flex: 1, maxWidth: 480 }}
              InputProps={{
                sx: {
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.85rem",
                },
              }}
              onKeyDown={(e) => e.key === "Enter" && handleRequestIdCheck()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleRequestIdCheck}
              disabled={requestChecking || !requestId.trim()}
              startIcon={
                requestChecking ? <CircularProgress size={16} /> : <Search />
              }
            >
              Check
            </Button>
          </Box>
          {requestError && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
              {requestError}
            </Alert>
          )}
          {requestResult !== null && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}
            >
              {requestResult ? (
                <>
                  <CheckCircle sx={{ color: "#34d399", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "#34d399" }}>
                    Request ID exists
                  </Typography>
                </>
              ) : (
                <>
                  <Cancel sx={{ color: "#94a3b8", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    Request ID not found
                  </Typography>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
